import { Inject } from '@odinjs/odin';
import { FieldAPI, FormAPI, InputAPI, TextAPI, ToastService} from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core'; 
import Controller, { OnBeforeCancel, OnChange, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpService, MessageService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { AbortableCloseEvent, ChangeEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import moment from 'moment';
import { FragmentNames, ScheduleSupplementsAliases } from '../../../../codegen';
import { FULL_LOCALE_DATE, ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Endpoint } from '../../../../constants/endpoints';
import { TimeUtils } from '../../../../utils/time-utils';
import BaseController from '../../../BaseController';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { MedicineAndSolutionsParams } from '../medicine-and-solutions/medicine-and-solutions-params';
import { Params, TimelineItemType } from '../administration-schedule/administration-schedule-types';
import { FoundationUtils } from '../../../../utils/foundation-utils';
import { ADEP_E1113Parameters, FunctionName } from '../../../../utils/parameters';
import DialogService from '../../../../services/dialog-service';

interface ReasonResponse {
  reasonDescription: string,
  sequenceNumber: number
}

interface ParamsRequest {
  establishment?: string,
  itemQuantity?: number,
  itemSequence?: number,
  justificationValue?: string,
  prescriptionNumber?: number,
  prescriptionsNumber?: number,
  reasonValue?: number
}

interface ResponseBody {
  consistencyDescription:  string;
}

interface SaveSchedulePrescriptionResponse {
  inconsistencyType: string,
  inconsistentDescription: string
}

enum ScheduleItemsParams {
  PREVENT           = 'I',
  DONT_VALIDATE     = 'N',
  COMMUNICATE       = 'C',
  PREVENT_IF_ACMSN  = 'A'
}

enum InconsistencyTypes {
  DOSAGE            = 'D',
  PESCRIPTIONS      = 'P',
  TIME              = 'H'
}

@Controller({ component: ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_LAYOUT, fragment: FragmentNames.SCHEDULE_SUPPLEMENTS })
export default class ScheduleSupplementsController extends BaseController {
  @Inject(ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_CONTENT_FORM)
  private formMedicationScheduleApi!: FormAPI;

  @Inject(ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_CONTENT_TEXT)
  private medicationScheduleContentText!: TextAPI;

  private medicineAndSolutions = MedicineAndSolutionsParams.params;

  private state = {
    userName: foundationContext.getUser()?.username
  } as Record<string, Value>;

  private scheduledSaved(saveResponseBody: SaveSchedulePrescriptionResponse): void {
    switch (saveResponseBody?.inconsistencyType) {
      case InconsistencyTypes.PESCRIPTIONS:
        this.showToast('tws.schedule-medication-infusion.no-prescription-in-datehour-error', ToastLevel.WARNING);
        this.formMedicationScheduleApi.setDisabled(false);
        break;
      case InconsistencyTypes.DOSAGE:
        this.showToast(saveResponseBody?.inconsistentDescription, ToastLevel.WARNING);
        this.formMedicationScheduleApi.setDisabled(false);
        break;
      case InconsistencyTypes.TIME:
        this.showToast('tws.schedule-supplements-time-no-prescripton', ToastLevel.WARNING);
        this.formMedicationScheduleApi.setDisabled(false);
        break;
      default:
        this.showToast('text.item-scheduled-successfully', ToastLevel.SUCCESS);
        applicationContext.popNavigationItem();
    }
  }

  private setSaveSchedulePrescription(body: Record<string, Value>): void {
    this.formMedicationScheduleApi.setLoading(true);
    HttpService.post(Endpoint.ADEP_SAVE_SCHEDULE_PRESCRIPTION, {}, { body }).then(response => {
      const saveResponseBody = response.body as SaveSchedulePrescriptionResponse;
      this.scheduledSaved(saveResponseBody);
    }).catch((err) => {
      const message = err.body?.errors[0].replace(/#*/, '');
      return err.body?.errors && this.showToast(String(message), ToastLevel.ERROR);
    }).finally(() => this.formMedicationScheduleApi.setLoading(false))
  }

  private mountBodySaveSchedule(params: ParamsRequest): Record<string, Value> {
    const dateTime = this.context.get('scheduleDate') as moment.Moment;
    const cpoeSequence = this.medicineAndSolutions?.cpoeSequence;
    const encounterNumber = Number(this.medicineAndSolutions?.encounterId);
    const groupingNumber = Number(this.medicineAndSolutions?.nrGroupingId);
    const cdInterval = this.medicineAndSolutions?.cdIntervalId;
    const intervalCode = cdInterval ? cdInterval : '';
    const itemCode = String(this.medicineAndSolutions?.cdMaterialId);
    const unitMeasurementCode = this.medicineAndSolutions?.uom;
    const viaApplicationType = this.medicineAndSolutions?.ieViaApplicationId;
    const acmSnStatus = this.medicineAndSolutions?.ieTypeItem === TimelineItemType.ENTERAL_NUTRITION ? Params.NOT_ALLOWED : Params.ALLOWED;

    return {
      acmSnStatus,
      calledBloodGlucoseStatus: Params.NOT_ALLOWED,
      componentsDescription: null,
      cpoeSequence,
      encounterNumber,
      establishmentCode: params.establishment,
      generateEventType: null,
      groupingNumber,
      hourSequence: null,
      inconsistencyType: null,
      inconsistentDescription: null,
      intervalCode,
      itemCode,
      itemQuantity: params.itemQuantity,
      itemSequence: params.itemSequence,
      itemType: this.medicineAndSolutions?.ieTypeItem,
      justificationDescription: params.justificationValue,
      laboratoryStatus: Params.NOT_ALLOWED,
      objectSequence: null,
      prescriptionNumber: params.prescriptionNumber,
      prescriptionsNumber: params.prescriptionsNumber,
      procInternalSequence: null,
      protBloodGlucoseNumber: null,
      questionSequence: null,
      reasonSequence: params.reasonValue,
      scheduleIntervStatus: Params.NOT_ALLOWED,
      signatureSequence: null,
      timeHour: dateTime?.format(ISO_FULL_DATE_FORMAT),
      unitMeasurementCode,
      userName: String(this.state.userName),
      viaApplicationType
    }
  }

  private saveScheduleSupplement(): void {
    const reasonValue = this.context.get('autoCompleteValue');
    const justificationValue = this.context.get('textAreaValue');
    const establishment = foundationContext.getEstablishment();
    const itemQuantity = Number(this.medicineAndSolutions?.qtDoseId);
    const itemSequence = Number(this.medicineAndSolutions?.nrSequenceId);
    const prescriptionNumber = Number(this.medicineAndSolutions?.nrPrescriptionId);
    const prescriptionsNumber = Number(this.medicineAndSolutions?.nrPrescriptionsId);

    const params = {
      establishment: establishment?.id,
      itemQuantity,
      itemSequence,
      justificationValue,
      prescriptionNumber,
      prescriptionsNumber,
      reasonValue,
    } as ParamsRequest;

    const body = this.mountBodySaveSchedule(params);
    this.setSaveSchedulePrescription(body);
  }

  private showModalConfirmation(description: string): void {
    const dialogExp = MessageService.translate('txt.list-wish-to-proceed');
    const newExp = dialogExp.replace('#@LISTA#@', description);
    DialogService.open({
      descriptionDialog: newExp,
      onClickCancel: () => { },
      onClickOk: () => { this.saveScheduleSupplement() },
      titleDialog: 'msg.wish-continue',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'tws.steps.confirm',
    });
  }

  private validateResults(firstCondition: boolean, secondCondition: boolean, thirdCondition: boolean, description: string): void {
    if (firstCondition) {
      this.saveScheduleSupplement();
      return;
    }
    if (secondCondition) {
      this.showModalConfirmation(description);
      return;
    }
    if (thirdCondition) {
      this.showToast(String(description), ToastLevel.ERROR);
      return;
    }
  }

  private verifyReturnProcedure(description: string | undefined): void {
    const resultParameter = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.ALLOW_ACCESS_ADEP);
    const shouldSaveScheduleSupplement = !description || (description && (resultParameter === ScheduleItemsParams.DONT_VALIDATE || resultParameter === ScheduleItemsParams.PREVENT_IF_ACMSN));
    const validateComunicate = description && resultParameter === ScheduleItemsParams.COMMUNICATE;
    const validatePrevent = description && resultParameter === ScheduleItemsParams.PREVENT;

    this.validateResults(shouldSaveScheduleSupplement as boolean,
      validateComunicate as boolean, validatePrevent as boolean,
      description as string);
  }

  private showToast(messge: string, typeMessage: ToastLevel): void {
    ToastService.open({
      dismissExpression: MessageService.translate('tws-ok.ok-text'),
      dismissible: true,
      highlight: false,
      id: generateUUID(),
      level: typeMessage,
      message: messge,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    });
  }

  private verifySchedule(body: Record<string, Value>): void {
    this.formMedicationScheduleApi.setLoading(true);
    HttpService.post(Endpoint.ADEP_VERIFY_SCHEDULE_MEDICATIONS, {}, {body})
      .then((res) => {
        const response = res.body as ResponseBody;
        this.verifyReturnProcedure(response?.consistencyDescription);
      })
      .catch((err) => {
        const message = err.body?.errors[0].replace(/#*/, '');
        return err.body?.errors && this.showToast(String(message), ToastLevel.ERROR);
      }).finally(() => this.formMedicationScheduleApi.setLoading(false))
  }

  private confirmScheduleSupplements(): void {
    const dateTime = this.context.get('scheduleDate') as moment.Moment;
    const establishment = foundationContext.getEstablishment();
    const param22 = FoundationUtils.findModuleParam(FunctionName.ADEP_1113, ADEP_E1113Parameters.PRESCRIPTION_HOUR_QUANTITY);
    const cdInterval = this.medicineAndSolutions?.cdIntervalId;
    const intervalCode = cdInterval ? cdInterval : '';
    const profileCode = foundationContext.getProfile()?.id;

    const body: Record<string, Value> = {
      acmSnStatus: Params.ALLOWED,
      cpoeSequence: this.medicineAndSolutions?.cpoeSequence,
      encounterNumber: this.medicineAndSolutions?.encounterId,
      establishmentCode: establishment?.id,
      intervalCode,
      itemCode: this.medicineAndSolutions?.cdMaterialId,
      itemQuantity: this.medicineAndSolutions?.qtDoseId,
      itemSequence: this.medicineAndSolutions?.nrSequenceId,
      itemType: this.medicineAndSolutions?.ieTypeItem,
      prescriptionNumber: this.medicineAndSolutions?.nrPrescriptionId,
      prescriptionQuantityHour: param22 as unknown as number,
      prescriptionsNumber: this.medicineAndSolutions?.nrPrescriptionsId,
      profileCode,
      timeDate: dateTime?.format(ISO_FULL_DATE_FORMAT),
      unitMeasurementCode: this.medicineAndSolutions?.uom,
      userName: String(this.state.userName),
      viaApplicationType: this.medicineAndSolutions?.ieViaApplicationId,
    }

    return this.verifySchedule(body);
  }

  @OnMount({ target: ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_LAYOUT })
  handleMountScheduleLayout(): void {
    InformationBarUtils.collapsedBar = true;
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    this.context.set({encounterId, patientId});
  }

  @OnMount({target: ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_CONTENT_TEXT })
  handleMountScheduleText(): void {
    const scheduleMedicationParams = MedicineAndSolutionsParams.params?.dsMaterialId;
    this.medicationScheduleContentText.setValue(String(scheduleMedicationParams));
  }

  @OnMount({target: ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_CONTENT_FORM})
  handleMountScheduleForm(): void {
    const scheduleDate = moment();
    this.formMedicationScheduleApi.setData({ dateFeld: scheduleDate.format(FULL_LOCALE_DATE) });
    const establishment = foundationContext.getEstablishment()?.id;
    this.context.set({ establishment, scheduleDate });
  }

  @OnMount({ target: ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_INPUT_DATE_TIME_FIELD })
  handleMountScheduleDateTime(event: MountEvent): void {
    TimeUtils.setDateToInput(event.target as InputAPI)
      .then(date => this.context.set({ scheduleDate: moment(date) }))
  }

  @OnChange({ target: ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_INPUT_DATE_TIME_FIELD })
  handleChangeScheduleDateTime(evt: ChangeEvent<FieldAPI>): void {
    const updatedScheduleDate = moment(String(evt.value));
    this.context.set({scheduleDate: updatedScheduleDate});
  }

  @OnChange({ target: ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_CONTENT_AUTOCOMPLETE })
  handleChangeScheduleAutoComplete(event: ChangeEvent<FieldAPI>): void {
    const reason = event.value as unknown as ReasonResponse;
    this.context.set({
      autoCompleteValue: reason.sequenceNumber,
    });
  }

  @OnChange({ target: ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_CONTENT_TEXT_AREA })
  handleChangeScheduleTextArea(event: ChangeEvent<FieldAPI>): void {
    this.context.set({
      textAreaValue: event.value,
    });
  }

  @OnBeforeCancel({ target: ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_CONTENT_FORM })
  handleBeforeCancelScheduleForm(event: AbortableCloseEvent): void {
    event.abort();
    applicationContext.popNavigationItem();
  }

  @OnSubmit(ScheduleSupplementsAliases.SUPPLEMENTS_SCHEDULE_CONTENT_FORM)
  handleSubmitScheduleSupplment(): void {
    this.confirmScheduleSupplements();
  }
}